/* LogsViewRenderer.js */

"use strict";
const _ = require("lodash");
const Registrations = require("../../../../../../../RegistrationNamespaces");
const BaseLogsViewRenderer = require("../../../../../../lib/logs/BaseLogsViewRenderer");
const FileName = require("@gluenetworks/steve_utilities").fileName;

class LogsViewRenderer extends BaseLogsViewRenderer {
    constructor(model) {
        super(model);
        this._rootElem = document.createElement("logs-view-gl");
        this._rootElem.classList.add("logs-control-root");
        this._rootElem.component = this._model;
        this._rootElem.appendChild(this._logsControl.renderer.getRootElement());
        this._logFile = "";
        this._logFileStarttime = undefined;
        this._actionsRegistered = false;
        this._fileNamer = FileName;
        this._registerListeners();
        this._resizeLogsControl();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._logFile = "";
            this._logFileStarttime = undefined;
            this._fileNamer = undefined;
            super._shutdown().then(() => {
                this._rootElem.remove();
                resolve();
            });
        });
        return prom;
    }

    _registerViewActions() {
        if (!this._actionsRegistered) {
            super._registerViewActions();
            this._model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Back",
                name: "back",
                handler: this._onBackButtonClick.bind(this),
            });
            this._model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Download",
                name: "cancel",
                handler: this._onDownloadButtonClick.bind(this),
            });
            this._actionsRegistered = true;
        }
    }

    _registerListeners() {
        super._registerListeners();
        this._model.on("LogsAdded", (this._onLogsAdded = this._onLogsAdded.bind(this)));
    }
    _unregisterListeners() {
        super._unregisterListeners();
        this._model.removeAllListeners("LogsAdded");
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._model
            .whenModelIsReady()
            .then(() => {
                this._setLogs(this._model.getLogs());
            })
            .catch((err) => {
                const msg = `Error: ${err.errorCode}::${err.errorMsg}`;
                const errLog = {
                    lln: 1,
                    time: new Date().getTime(),
                    lvl: "ERROR",
                    msg,
                };
                this._setLogs([errLog]);
                this._model.gameEditor.disableViewAction("cancel");
            });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _onLogsAdded(logs) {
        this._appendLogs(logs);
    }

    dataToInt8Arr(data) {
        const byteString = window.atob(data);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const int8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            int8Array[i] = byteString.charCodeAt(i);
        }
        return int8Array;
    }

    _onDownloadButtonClick() {
        if (!_.isEmpty(this._logFile)) {
            this.model.gameEditor.disableViewAction("cancel");
            this._model._modalUserFeedback
                .displayLoading({message: "Download in progress..."})
                .then(() => {
                    return this.model.retrieveJob();
                })
                .then((jobEntity) => {
                    return jobEntity.runJobBundleWork();
                })
                .then((data) => {
                    const fileData = _.get(data, "summary.bundle", "");
                    return this.model.io.fileFactory.saveArrayBufferAsFile(
                        this.dataToInt8Arr(fileData),
                        this._fileNamer({
                            solution: "DM",
                            general: "DeviceActivity",
                            specific: this._model.device.name,
                            date: this._logFileStarttime,
                            suffix: "zip",
                        }),
                        "application/zip"
                    );
                })
                .catch((error) => {
                    this._model._logger.warn({error}, "Device onDownloadButtonClick - error");
                    this._model._modalUserFeedback.hideDisplayed().then(() => {
                        this._model._modalUserFeedback.displayError(error);
                    });
                    return Promise.reject(error);
                })
                .then(() => {
                    return this._model._modalUserFeedback.hideDisplayed();
                })
                .finally(() => {
                    this.model.gameEditor.enableViewAction("cancel");
                    return Promise.resolve();
                });
        }
    }

    _onBackButtonClick() {
        this.model.gameEditor.setActiveView(Registrations.ODM_OS_PLATFORM_EDITOR_VIEW_ACTIVITY, [
            {
                value: {
                    fqn: Registrations.ODM_OS_PLATFORM_EDITOR_SUBVIEW_ACTIVITY_LOGS,
                },
            },
        ]);
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _setLogs(newVal) {
        if (!_.isUndefined(newVal)) {
            this._registerViewActions();
            this._logsControl.setLogs(newVal);
            this._appendLogFileLine(newVal);
        }
    }

    _appendLogs(logs) {
        this._registerViewActions();
        this._logsControl.appendLogs(logs);
        this._appendLogFileLine(logs);
    }

    _appendLogFileLine(logs) {
        let logsArray = _.isArray(logs) ? logs : [logs];
        for (let i = 0; i < logsArray.length; i++) {
            let logFileLine = this._createLogFileLine(logsArray[i]);
            this._logFile += logFileLine.join(" ") + "\n";
        }
    }

    _createLogFileLine(log) {
        let logFileLine = [];
        logFileLine.push(_.get(log, "lln", ""));
        let timestamp = _.get(log, "time", undefined);
        let fileTimeStamp = timestamp;
        if (!_.isUndefined(timestamp)) {
            fileTimeStamp = Date.create(timestamp).format("{yyyy}-{MM}-{dd}T{HH}:{mm}:{ss}.{fff}");
            timestamp = Date.create(timestamp).format("{HH}:{mm}:{ss}.{fff}");
        } else {
            timestamp = "";
        }
        logFileLine.push(timestamp);
        if (!this._logFileStarttime) {
            this._logFileStarttime = fileTimeStamp;
        }
        let level = document.createElement("span");
        level.classList.add("level");
        let lvl = "";
        if (_.get(log, "resp", "")) {
            lvl = "RESP";
        } else if (_.get(log, "eventType", false)) {
            if (_.get(log, "eventType") === "START" || _.get(log, "eventType") === "FINISH") {
                lvl = _.get(log, "eventType");
            } else {
                lvl = "EVENT";
            }
        }
        level.innerHTML = lvl;
        logFileLine.push(lvl);
        let msg = _.has(log, "msg") ? log.msg : _.get(log, "resp", "");
        logFileLine.push(msg);
        return logFileLine;
    }

    _resizeLogsControl() {
        this._logsControl.setControlHeight(this._rootElem.clientHeight - 20);
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = LogsViewRenderer;
